import dayjs from "dayjs";
import da from "dayjs/locale/da";
import { FC, KeyboardEvent } from "react";
import { useLogeMembers } from "../../api/user-api";
import { Fine, LogeMember } from "../../types/entities";
import { Icon, IconGlyph } from "./Icon";

dayjs.locale(da);

export const PAID_VIA: Record<NonNullable<Fine["paid_via"]>, { label: string; icon: IconGlyph }> = {
  mobilepay: { label: "MobilePay", icon: "wallet" },
  cash: { label: "Kontant", icon: "coins" },
  bank: { label: "Bank", icon: "building-columns" },
};

/** "for sent – efter melding" → "For sent – efter melding". Whatever else the
 *  minister typed is left as typed; only the sentence gets a capital. */
export function sentence(text: string) {
  const trimmed = text.trim();
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

/** How long an unpaid fine has been hanging, in words that fit a chip. */
export function outstandingFor(createdAt: string) {
  const days = dayjs().startOf("day").diff(dayjs(createdAt).startOf("day"), "day");
  if (days <= 0) return "i dag";
  if (days === 1) return "1 dag";
  return `${days} dage`;
}

export function useFineMember(fine: Fine) {
  const { data: members } = useLogeMembers();
  return members?.find((m) => m.id === fine.loge_member_id);
}

/**
 * One fine, as a card of fixed height.
 *
 * Every card is the same size whatever was typed into it, so the list reads
 * as an even rhythm of cards rather than a ragged stack: the reason is held
 * to two lines, and the whole of it is a tap away in the detail sheet. The
 * card has three rows that always land in the same place — who and how much
 * on top, why in the middle, and status along the bottom edge — so the eye
 * can run straight down any one of them.
 */
const FineRow: FC<{ fine: Fine; canEdit?: boolean; onMarkPaid?: () => void; onClick?: () => void }> = ({
  fine,
  canEdit = true,
  onMarkPaid,
  onClick,
}) => {
  const member = useFineMember(fine);
  const name = member?.name ?? "—";
  const paid = fine.status === "paid";

  // A card can't be a <button> — it holds the BETAL button — so it takes the
  // role and keyboard handling itself.
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick?.();
    }
  };

  return (
    <div
      onClick={onClick}
      onKeyDown={onClick ? onKeyDown : undefined}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      className={`h-[148px] rounded-lg bg-card border border-line-soft/80 shadow-[0_1px_2px_rgba(40,30,20,0.06)] p-[18px] flex gap-4 transition-[transform,background-color] duration-150 ${
        onClick ? "cursor-pointer active:scale-[0.985] active:bg-card/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30" : ""
      }`}
    >
      <FineAvatar member={member} />

      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex items-baseline justify-between gap-3">
          <div className="font-display text-[16px] leading-[22px] font-bold uppercase tracking-tight truncate">
            {name}
          </div>
          <Amount value={fine.amount_kr} tone={paid ? "ink" : "clay"} />
        </div>

        <p className="text-[13.5px] leading-[19px] text-ink2 mt-1.5 line-clamp-2 break-words">
          {sentence(fine.reason)}
        </p>

        <div className="mt-auto flex items-center justify-between gap-2 h-7">
          <StatusChip fine={fine} />
          {paid ? (
            fine.paid_via && (
              <span className="inline-flex items-center gap-1.5 font-mono text-[9.5px] tracking-[1px] uppercase text-ink3">
                <Icon name={PAID_VIA[fine.paid_via].icon} size={10} />
                {PAID_VIA[fine.paid_via].label}
              </span>
            )
          ) : (
            canEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkPaid?.();
                }}
                aria-label={`Markér ${name}s bøde som betalt`}
                className="h-7 font-mono text-[10px] tracking-[1.1px] font-bold text-paper bg-vote-yes rounded-full pl-2.5 pr-3 flex items-center gap-1.5 active:scale-95 transition-transform"
              >
                <Icon name="check" size={10} />
                BETAL
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export function Amount({ value, tone, size = "text-[22px]" }: { value: number; tone: "ink" | "clay"; size?: string }) {
  return (
    <div
      className={`flex-shrink-0 font-display font-extrabold ${size} leading-none tracking-tight tabular-nums ${
        tone === "clay" ? "text-unpaid" : "text-ink"
      }`}
    >
      {value}
      <span className="font-mono text-[10px] font-medium tracking-wide text-ink3 ml-1">KR</span>
    </div>
  );
}

export function StatusChip({ fine }: { fine: Fine }) {
  if (fine.status === "paid") {
    return (
      <span className="h-6 inline-flex items-center gap-1.5 rounded-full bg-sage/10 text-sage pl-2 pr-2.5 font-mono text-[9.5px] tracking-[1px] uppercase font-semibold whitespace-nowrap">
        <Icon name="check" size={9} />
        Betalt{fine.paid_at && <span className="font-normal">{dayjs(fine.paid_at).format("D. MMM")}</span>}
      </span>
    );
  }
  return (
    <span className="h-6 inline-flex items-center gap-1.5 rounded-full bg-clay/10 text-clay pl-2 pr-2.5 font-mono text-[9.5px] tracking-[1px] uppercase font-semibold whitespace-nowrap">
      <span className="w-[5px] h-[5px] rounded-full bg-clay" />
      Ubetalt<span className="font-normal opacity-80">· {outstandingFor(fine.created_at)}</span>
    </span>
  );
}

/** The member's emoji in a coin, or their initial when they haven't picked one. */
export function FineAvatar({ member, size = "w-11 h-11" }: { member?: LogeMember; size?: string }) {
  return (
    <div
      aria-hidden
      className={`${size} rounded-full flex-shrink-0 bg-paper2/70 ring-1 ring-inset ring-line-soft flex items-center justify-center`}
    >
      {member?.emoji ? (
        <span className="text-[22px] leading-none">{member.emoji}</span>
      ) : (
        <span className="font-display text-[16px] font-extrabold uppercase text-ink2">
          {member?.name.charAt(0) ?? "?"}
        </span>
      )}
    </div>
  );
}

export default FineRow;
